import styled from "styled-components";
import { C3Text } from "../text/CustomText";
import { ContentWrapperMenuItems } from "./ContentWrapperComponents";
import CustomIcon from "../image/CustomIcon";
import { Column, Divider, Row, SizedBox } from "../CommonBaseComponents";
import Translate from "../../translate/Translate";
import { useRef, useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthService from "../../../service/AuthService";
import CustomPopup from "../popup/CustomPopup";
import Pagination from "../Pagination";
import LocalStorageKey from "../../enums/LocalStorageKey";
import { CustomAbsolutePositionDropdownWrapper } from "../dropdown/CustomDropdownWrapper";
import { Theme } from "../../Theme";

const BackgroundWrapper = styled.div`
    background-color: ${Theme.color.neutral100};
    height: ${({ isFixed }) => (isFixed ? "100vh" : "100%")};
    width: 100vw;
    display: flex;
    flex-direction: row;
`;

const MenuBarWrapper = styled.div`
    width: 272px;
    min-height: 100vh;
    max-height: ${({ isFixed }) => (isFixed ? "100vh" : undefined)};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 21px 12px 16px;
    box-sizing: border-box;
`;

const ContentWrapper = styled.div`
    width: 100%;
    margin-bottom: 15px;
    margin-top: 17px;
    margin-right: 16px;
    background-color: ${Theme.color.white};
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const MainContent = styled.div`
    flex: 1;
    overflow-y: auto;
`;

const IconWrapper = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 100%;
    background-color: ${Theme.color.neutral200};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

export function UserDropdownMenu({
    onClose,
    setPopupMessage,
    setIsPopupOpened,
    topPosition = "-1500%",
    leftPosition = "0",
}) {
    const navigate = useNavigate();
    const service = new AuthService();

    const handleLogout = async () => {
        try {
            await service.logout();
            navigate("/auth");
        } catch (error) {
            setPopupMessage(error.message);
            setIsPopupOpened(true);
        }
    };

    const handleDebug = () => {
        navigate("/debug");
    };

    return (
        <CustomAbsolutePositionDropdownWrapper
            top={topPosition}
            openedLeftSide={true}
            left={leftPosition}
            padding={"10px 0"}
        >
            <Row padding={"2px 12px"}>
                <IconWrapper>
                    <CustomIcon iconName={"user"} />
                </IconWrapper>
                <SizedBox width={"10px"} />
                <Column alignItem={"flex-start"} width={"150px"}>
                    <C3Text fontColor={Theme.color.black} useEllipsis width={"100%"}>
                        {localStorage.getItem(LocalStorageKey.displayUserID) ?? "email@email.com"}
                    </C3Text>
                    <C3Text fontColor={Theme.color.gray500}>
                        {Translate(`role.${localStorage.getItem(LocalStorageKey.role)}`)}
                    </C3Text>
                </Column>
            </Row>
            <Divider marginTop={"14px"} marginBottom={"4px"} />
            {/* debug start */}
            {/*<Row padding={"10px 18px"} cursor={"pointer"} onClick={handleDebug}>*/}
            {/*    <CustomIcon iconName={"key_vertical"} size={"18px"} cursor={"pointer"} />*/}
            {/*    <SizedBox width={"10px"} cursor={"pointer"} />*/}
            {/*    <Column alignItem={"flex-start"} cursor={"pointer"}>*/}
            {/*        <C3Text cursor={"pointer"}>디버그: 롤 변경</C3Text>*/}
            {/*    </Column>*/}
            {/*</Row>*/}
            {/*<Divider marginTop={"4px"} marginBottom={"4px"} />*/}
            {/* debug end */}
            <Row padding={"10px 18px"} cursor={"pointer"} onClick={() => navigate("/auth/reset-password/request")}>
                <CustomIcon iconName={"key_vertical"} size={"18px"} cursor={"pointer"} />
                <SizedBox width={"10px"} cursor={"pointer"} />
                <Column alignItem={"flex-start"} cursor={"pointer"}>
                    <C3Text cursor={"pointer"}>Change Password</C3Text>
                </Column>
            </Row>
            <Divider marginTop={"4px"} marginBottom={"4px"} thickness={"6px"} color={Theme.color.black_006} />
            <Row padding={"10px 18px"} cursor={"pointer"} onClick={handleLogout}>
                <CustomIcon iconName={"exit_to_app"} size={"18px"} cursor={"pointer"} />
                <SizedBox width={"10px"} cursor={"pointer"} />
                <Column alignItem={"flex-start"} cursor={"pointer"}>
                    <C3Text cursor={"pointer"}>Sign Out</C3Text>
                </Column>
            </Row>
        </CustomAbsolutePositionDropdownWrapper>
    );
}

function WebMenuBar({ isFixed }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const menuRef = useRef(null);
    useClickOutside(menuRef, () => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        }
    });

    // api 에러 팝업
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");

    return (
        <MenuBarWrapper isFixed={isFixed}>
            <Column alignItem={"flex-start"}>
                <Row>
                    <SizedBox width={"6px"} />
                    <CustomIcon iconName={"logo"} size={"32px"} />
                    <SizedBox width={"10px"} />
                    <C3Text fontWeight={"800"} letterSpacing={"-0.02rem"}>
                        {Translate("common.laundryCrew")} {Translate("role.admin")}
                    </C3Text>
                </Row>
                <SizedBox height={"24px"} />
                <ContentWrapperMenuItems />
            </Column>
            <Row justifyContent={"space-between"}>
                <Row>
                    <IconWrapper>
                        <CustomIcon iconName={"user"} />
                    </IconWrapper>
                    <SizedBox width={"10px"} />
                    <Column alignItem={"flex-start"} width={"150px"}>
                        <C3Text fontColor={Theme.color.gray500} useEllipsis width={"100%"}>
                            {localStorage.getItem(LocalStorageKey.displayUserID) ?? "email@email.com"}
                        </C3Text>
                        <C3Text fontColor={Theme.color.gray500} useEllipsis width={"100%"}>
                            {Translate(`role.${localStorage.getItem(LocalStorageKey.role)}`)}
                        </C3Text>
                    </Column>
                </Row>
                <MenuContainer ref={menuRef}>
                    <SizedBox width={"auto"} height={"auto"} padding={"0 24px"} onClick={toggleMenu} cursor={"pointer"}>
                        <CustomIcon iconName={"menu"} width={"2.25px"} height={"13.5px"} cursor={"pointer"} />
                    </SizedBox>
                    {isMenuOpen && (
                        <UserDropdownMenu
                            onClose={toggleMenu}
                            setPopupMessage={setPopupMessage}
                            setIsPopupOpened={setIsPopupOpened}
                        />
                    )}
                </MenuContainer>
            </Row>
            <CustomPopup isOpen={isPopupOpened} onConfirm={() => setIsPopupOpened(false)} message={popupMessage} />
        </MenuBarWrapper>
    );
}

export function WebContentWrapper({ children, isFixed, hasPagination }) {
    // for pagination
    // 현재 페이지의 단일 출처(single source of truth)는 URL(?page=)이다.
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = Number(searchParams.get("page")) || 1;
    const totalPages = Number(searchParams.get("totalPage")) || 1;

    const handlePageChange = (page) => {
        // 다른 콜백이 들고 있는 오래된 스냅샷에 덮어쓰이지 않도록 함수형 업데이터 사용
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("page", page);
                return next;
            },
            { replace: true }
        );
    };

    return (
        <BackgroundWrapper isFixed={isFixed}>
            <WebMenuBar isFixed={isFixed} />
            <ContentWrapper>
                <MainContent>{children}</MainContent>
                {hasPagination && (
                    <Row justifyContent={"center"} padding={"16px 0"}>
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                    </Row>
                )}
            </ContentWrapper>
        </BackgroundWrapper>
    );
}
